"use client";

import type React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Container,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Link,
  Card,
  CardBody,
} from "@chakra-ui/react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const PasswordReset: React.FC = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const { error: resetError } = await resetPassword(email);

      if (resetError) {
        setError(
          resetError.message || "Failed to send reset email. Please try again."
        );
        return;
      }

      setMessage("Check your email for the verification code!");

      // Navigate to OTP page after showing success message
      setTimeout(() => {
        navigate("/password-reset-otp", { state: { email } });
      }, 2000);
    } catch (err: any) {
      console.log("[v0] Password reset error:", err);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="gray.50"
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Container maxW="md">
        <Card>
          <CardBody p={8}>
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <VStack spacing={2} textAlign="center">
                <Box
                  as="img"
                  src={process.env.REACT_APP_MAIN_LOGO_URL}
                  alt="App Logo"
                  h="100px"
                  mx="auto"
                  mb={2}
                />
                <Text color="gray.600">Reset your password</Text>
              </VStack>

              {/* Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <FormControl isRequired>
                    <FormLabel color="gray.700">Email Address</FormLabel>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Enter your email address"
                      bg="white"
                      borderColor="gray.300"
                      _focus={{
                        borderColor: "primary.500",
                        boxShadow: "0 0 0 1px primary.500",
                      }}
                    />
                  </FormControl>

                  {error && (
                    <Alert status="error" borderRadius="md">
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}

                  {message && (
                    <Alert status="success" borderRadius="md">
                      <AlertIcon />
                      {message}
                    </Alert>
                  )}

                  <Button
                    type="submit"
                    variant="brandGradient"
                    size="lg"
                    width="full"
                    isLoading={loading}
                    loadingText="Sending reset link..."
                  >
                    Send Reset OTP
                  </Button>
                </VStack>
              </form>

              {/* Back to Sign In */}
              <Text textAlign="center" fontSize="sm" color="gray.600">
                Remember your password?{" "}
                <Link
                  as={RouterLink}
                  to="/signin"
                  color="primary.500"
                  fontWeight="medium"
                >
                  Sign in
                </Link>
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    </Box>
  );
};

export default PasswordReset;
